import { PlotArea, HAIRLINE_STROKE } from './frame.js';
import { svgEl } from './svg.js';

export type TooltipRow = {
  value: string;
  /** Empty label renders the row as a title (e.g. the month). */
  label?: string;
  color?: string;
};

export type CrosshairOptions = {
  svg: SVGSVGElement;
  host: HTMLElement;
  plot: PlotArea;
  count: number;
  xForIndex: (index: number) => number;
  renderTooltip: (index: number) => TooltipRow[];
  onIndex?: (index: number | null) => void;
};

export type Crosshair = {
  /** Moves the crosshair; `announce` also updates the screen-reader status line. */
  setIndex: (index: number | null, announce?: boolean) => void;
  destroy: () => void;
};

type ViewBox = { x: number; y: number; width: number; height: number };

function readViewBox(svg: SVGSVGElement): ViewBox {
  const parts = (svg.getAttribute('viewBox') ?? '').split(/[\s,]+/).map(Number);
  if (parts.length === 4 && parts.every(Number.isFinite)) {
    return { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
  }
  return { x: 0, y: 0, width: 1, height: 1 };
}

/** Maps between client pixels and SVG user units for `preserveAspectRatio="xMidYMid meet"`. */
function geometry(svg: SVGSVGElement) {
  const rect = svg.getBoundingClientRect();
  const viewBox = readViewBox(svg);
  const scale = rect.width > 0 && rect.height > 0
    ? Math.min(rect.width / viewBox.width, rect.height / viewBox.height)
    : 0;
  const offsetX = scale > 0 ? (rect.width - viewBox.width * scale) / 2 : 0;
  const offsetY = scale > 0 ? (rect.height - viewBox.height * scale) / 2 : 0;
  return {
    toUserX: (clientX: number) => (scale > 0 ? viewBox.x + (clientX - rect.left - offsetX) / scale : viewBox.x),
    toClientX: (userX: number) => rect.left + offsetX + (userX - viewBox.x) * scale,
    toClientY: (userY: number) => rect.top + offsetY + (userY - viewBox.y) * scale,
  };
}

function nearestIndex(xs: number[], x: number): number {
  let lo = 0;
  let hi = xs.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (xs[mid] < x) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(xs[lo - 1] - x) <= Math.abs(xs[lo] - x)) return lo - 1;
  return lo;
}

function renderRows(tooltip: HTMLElement, rows: TooltipRow[]): void {
  tooltip.replaceChildren();
  rows.forEach(row => {
    const line = document.createElement('div');
    line.className = row.label ? 'chart-tooltip-row' : 'chart-tooltip-row is-title';
    const swatch = document.createElement('span');
    swatch.className = 'chart-tooltip-swatch';
    if (row.color) swatch.style.background = row.color;
    else swatch.classList.add('is-empty');
    const value = document.createElement('strong');
    value.className = 'chart-tooltip-value';
    value.textContent = row.value;
    line.append(swatch, value);
    if (row.label) {
      const label = document.createElement('span');
      label.className = 'chart-tooltip-label';
      label.textContent = row.label;
      line.append(label);
    }
    tooltip.append(line);
  });
}

/**
 * Snapping crosshair with a shared HTML tooltip. Pointer events snap to the nearest index,
 * keyboard moves the index (←/→, Home/End, Escape hides). The tooltip is clamped to the host
 * and flipped to the left of the hairline near the right edge.
 */
export function attachCrosshair(options: CrosshairOptions): Crosshair {
  const { svg, host, plot, count, xForIndex, renderTooltip, onIndex } = options;
  const xs: number[] = [];
  for (let i = 0; i < count; i += 1) xs.push(xForIndex(i));

  host.style.position = 'relative';

  const hairline = svgEl('line', {
    class: 'chart-crosshair',
    y1: plot.y0.toFixed(1),
    y2: plot.y1.toFixed(1),
    stroke: HAIRLINE_STROKE,
    'stroke-width': 1,
    'pointer-events': 'none',
    visibility: 'hidden',
  });
  svg.append(hairline);

  const tooltip = document.createElement('div');
  tooltip.className = 'chart-tooltip';
  tooltip.setAttribute('aria-hidden', 'true');
  tooltip.hidden = true;
  host.append(tooltip);

  // Visually hidden status line: announces keyboard navigation only, never pointer hover.
  const live = document.createElement('div');
  live.className = 'chart-live';
  live.setAttribute('role', 'status');
  live.setAttribute('aria-live', 'polite');
  host.append(live);

  let current: number | null = null;
  let pointerClientY: number | null = null;

  const position = (index: number) => {
    const geo = geometry(svg);
    const hostRect = host.getBoundingClientRect();
    const anchorX = geo.toClientX(xs[index]) - hostRect.left;
    const anchorY = (pointerClientY ?? geo.toClientY(plot.y0)) - hostRect.top;
    const gap = 12;
    const width = tooltip.offsetWidth;
    const height = tooltip.offsetHeight;
    const hostWidth = host.clientWidth || hostRect.width;
    const hostHeight = host.clientHeight || hostRect.height;

    let left = anchorX + gap;
    if (left + width > hostWidth) left = anchorX - gap - width;
    left = Math.max(0, Math.min(left, Math.max(0, hostWidth - width)));

    let top = anchorY - height / 2;
    top = Math.max(0, Math.min(top, Math.max(0, hostHeight - height)));

    tooltip.style.left = `${Math.round(left)}px`;
    tooltip.style.top = `${Math.round(top)}px`;
  };

  const setIndex = (index: number | null, announce = false) => {
    if (index !== null && (count === 0 || Number.isNaN(index))) index = null;
    if (index !== null) index = Math.max(0, Math.min(count - 1, index));
    current = index;
    if (index === null) {
      hairline.setAttribute('visibility', 'hidden');
      tooltip.hidden = true;
      if (announce) live.textContent = '';
      onIndex?.(null);
      return;
    }
    const x = xs[index].toFixed(1);
    hairline.setAttribute('x1', x);
    hairline.setAttribute('x2', x);
    hairline.setAttribute('visibility', 'visible');
    const rows = renderTooltip(index);
    renderRows(tooltip, rows);
    tooltip.hidden = false;
    position(index);
    if (announce) {
      live.textContent = rows.map(row => (row.label ? `${row.label}: ${row.value}` : row.value)).join('. ');
    }
    onIndex?.(index);
  };

  const fromPointer = (event: PointerEvent) => {
    if (!count) return;
    const geo = geometry(svg);
    const x = geo.toUserX(event.clientX);
    pointerClientY = event.clientY;
    setIndex(nearestIndex(xs, x));
  };

  const onLeave = () => {
    pointerClientY = null;
    setIndex(null);
  };

  const onKey = (event: KeyboardEvent) => {
    if (!count) return;
    const base = current ?? -1;
    let next: number | null | undefined;
    switch (event.key) {
      case 'ArrowRight':
        next = base < 0 ? 0 : base + 1;
        break;
      case 'ArrowLeft':
        next = base < 0 ? count - 1 : base - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      case 'Escape':
        next = null;
        break;
      default:
        return;
    }
    event.preventDefault();
    pointerClientY = null;
    setIndex(next, true);
  };

  svg.addEventListener('pointermove', fromPointer);
  svg.addEventListener('pointerdown', fromPointer);
  svg.addEventListener('pointerleave', onLeave);
  svg.addEventListener('keydown', onKey);
  svg.addEventListener('blur', onLeave);

  return {
    setIndex,
    destroy: () => {
      svg.removeEventListener('pointermove', fromPointer);
      svg.removeEventListener('pointerdown', fromPointer);
      svg.removeEventListener('pointerleave', onLeave);
      svg.removeEventListener('keydown', onKey);
      svg.removeEventListener('blur', onLeave);
      hairline.remove();
      tooltip.remove();
      live.remove();
    },
  };
}
